/**
 * Collection authorization (TMMRSL-0080 Unit 2, TMMAPI-0162 U5): collect a negative ledger balance
 * from the card already on file. OWNER authorizes (`POST { version, accept: true }`) and withdraws
 * (`DELETE`). STAFF and ADMIN see the state only. This sends no card data. A failed authorize or
 * withdraw reloads the state (TMMRSL-0081); a withdraw that pay-api has not confirmed yet is already
 * withdrawn.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { backendCode } from '@/api/errors';
import {
  collectionPresentation,
  resellerBillingApi,
  type CollectionAuthorizationView,
} from '@/api/resellerBilling';
import { useIsOwner } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useActionError } from '@/components/useActionError';
import type { RemoteState } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { collectionAuthorizeError } from './collectionErrors';

export interface CollectionSectionProps {
  state: RemoteState<CollectionAuthorizationView>;
  /**
   * `true` when a card is on file, `false` when the card read is ready and empty, `null` while
   * that read is still unknown. Authorize is withheld only for `false`.
   */
  cardOnFile: boolean | null;
}

function PayoutAccountLink() {
  const { t } = useTranslation();
  return (
    <Button component={RouterLink} to="/settlements#payout-method" color="inherit" size="small">
      {t('payouts.method.title')}
    </Button>
  );
}

function CardOnFileButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button color="inherit" size="small" onClick={onClick}>
      {t('resellerBilling.card.title')}
    </Button>
  );
}

function collectionErrorAction(err: unknown, onShowCard: () => void): ReactNode {
  const code = backendCode(err);
  if (code === 'PAYEE_NOT_FOUND') return <PayoutAccountLink />;
  if (code === 'COLLECTION_CARD_REQUIRED') return <CardOnFileButton onClick={onShowCard} />;
  return null;
}

/** The card section is already on this page, under the dialog, so a hash does not scroll it. */
function revealCardOnFile() {
  const section = document.getElementById('card-on-file');
  const heading = document.getElementById('card-on-file-heading');
  section?.scrollIntoView();
  if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
}

export function CollectionSection({ state, cardOnFile }: CollectionSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const isOwner = useIsOwner();
  const describeAuthorize = useActionError(collectionAuthorizeError);
  const describeWithdraw = useActionError();
  const [authorizing, setAuthorizing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const revealCard = useRef(false);
  // After the dialog's focus restore (its effect cleanup), move focus to the card heading.
  useEffect(() => {
    if (authorizing || !revealCard.current) return;
    revealCard.current = false;
    revealCardOnFile();
  }, [authorizing]);

  function showCardOnFile() {
    revealCard.current = true;
    setAuthorizing(false);
  }
  const view = state.status === 'ready' ? state.data : null;
  const presentation = view ? collectionPresentation(view) : null;
  const termsVersion = view?.termsVersion ?? '';
  const offerAuthorize =
    presentation === 'notAuthorized' ||
    presentation === 'lapsed' ||
    (presentation === 'authorized' && view?.reauthorizationRequired === true);
  const offerWithdraw = presentation === 'authorized';
  const noCard = cardOnFile === false;

  async function authorize() {
    try {
      await resellerBillingApi.authorizeCollection(termsVersion);
      toast(t('resellerBilling.collection.authorizedDone'));
    } finally {
      state.reload();
    }
  }

  async function withdraw() {
    try {
      await resellerBillingApi.revokeCollectionAuthorization();
      toast(t('resellerBilling.collection.withdrawnDone'));
    } catch (err) {
      // Already withdrawn. Close the dialog (don't rethrow) and say so outside it.
      if (backendCode(err) === 'PAY_API_UNAVAILABLE') {
        toast(t('resellerBilling.collection.error.withdrawUnconfirmed'), 'warning');
        return;
      }
      throw err;
    } finally {
      state.reload();
    }
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2">
        {t('resellerBilling.collection.title')}
      </Typography>
      {state.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')} sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={72} />
        </Box>
      ) : null}
      {state.status === 'error' ? (
        <ApiErrorState
          what={t('errors.what.collection')}
          error={state.error}
          onRetry={state.reload}
        />
      ) : null}
      {state.status === 'ready' && view && presentation ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
            {t(`resellerBilling.collection.${presentation}`)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {view.revocationReason === 'CARD_REPLACED'
              ? t('resellerBilling.collection.cardReplaced')
              : t(`resellerBilling.collection.${presentation}Detail`)}
          </Typography>
          {isOwner ? (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
              {offerAuthorize && termsVersion.length > 0 ? (
                noCard ? (
                  <Typography variant="body2">{t('resellerBilling.collection.needsCard')}</Typography>
                ) : (
                  <Button variant="contained" onClick={() => setAuthorizing(true)}>
                    {t('resellerBilling.collection.authorize')}
                  </Button>
                )
              ) : null}
              {offerWithdraw ? (
                <Button variant="outlined" onClick={() => setWithdrawing(true)}>
                  {t('resellerBilling.collection.withdraw')}
                </Button>
              ) : null}
            </Box>
          ) : (
            <Typography variant="caption" component="p" sx={{ mt: 1, color: 'text.secondary' }}>
              {t('resellerBilling.collection.ownerOnly')}
            </Typography>
          )}
        </>
      ) : null}
      <ConfirmDialog
        open={authorizing}
        title={t('resellerBilling.collection.authorizeTitle')}
        consequence={t('resellerBilling.collection.authorizeBody')}
        confirmLabel={t('resellerBilling.collection.authorize')}
        onConfirm={authorize}
        onCancel={() => setAuthorizing(false)}
        errorMessage={(err) => describeAuthorize(err).message}
        errorAction={(err) => collectionErrorAction(err, showCardOnFile)}
      />
      <ConfirmDialog
        open={withdrawing}
        title={t('resellerBilling.collection.withdrawTitle')}
        consequence={t('resellerBilling.collection.withdrawBody')}
        confirmLabel={t('resellerBilling.collection.withdraw')}
        onConfirm={withdraw}
        onCancel={() => setWithdrawing(false)}
        errorMessage={(err) => describeWithdraw(err).message}
      />
    </Paper>
  );
}
